import { z } from "zod";
import {
  BUSINESS_BRANCHES,
  CRTS,
  IE_INDICATORS,
  MAX_DOCUMENT_CENTS,
  MAX_MVA_PCT4,
  MAX_PRICE_CENTS,
  MAX_STOCK_QTY_Q4,
  MAX_TAX_ITEMS,
  REVENUE_BANDS,
  TAX_DEST_UFS,
  TAX_REGIMES,
  WIZARD_ACTIVITIES,
  BRAZIL_UFS,
  type Crt,
  type DestUf,
  type IeIndicator,
  type TaxResult,
  type TaxRuleDraft,
} from "@cx/domain";
import { listQuery } from "./common";
import { companyCnpjInput } from "./fiscal";
import { blank, text } from "./fields";
import { ulidParam } from "./ids";

// ===== Naturezas de operação, regras de tributação e simulação de impostos (T2.2, RF-VEN-04) =====

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);
const pct4 = (max = 1_000_000) =>
  nullish(
    z
      .number("Informe o percentual")
      .int("Percentual inválido")
      .min(0, "Percentual não pode ser negativo")
      .max(max, `Percentual até ${(max / 10_000).toLocaleString("pt-BR")}%`),
  );
const code = (re: RegExp, msg: string) =>
  nullish(
    z
      .string()
      .trim()
      .transform((v) => v || null)
      .pipe(z.string().regex(re, msg).nullable()),
  );
const cents = (what: string, max = MAX_DOCUMENT_CENTS) =>
  z
    .number(`Informe ${what}`)
    .int("Use centavos inteiros")
    .min(0, "Valor não pode ser negativo")
    .max(max, "Valor acima do limite");

export const TAX_OPERATION_PURPOSES = [1, 2, 3, 4] as const;
export const PRESENCE_INDICATORS = [0, 1, 2, 3, 4, 5, 9] as const;
export const TAX_DOC_MODELS = ["55", "65"] as const;

export const taxOperationInput = z.object({
  name: z.string("Informe o nome").trim().min(1, "Informe o nome").max(120, "Nome com até 120 caracteres"),
  direction: z.enum(["out", "in"], "Escolha saída ou entrada"),
  purpose: z.literal(TAX_OPERATION_PURPOSES, "Finalidade inválida").default(1),
  docModel: z.enum(TAX_DOC_MODELS, "Modelo inválido").default("55"),
  series: z
    .number("Informe a série")
    .int("Série inválida")
    .min(0, "Série de 0 a 999")
    .max(999, "Série de 0 a 999")
    .default(1),
  presenceIndicator: z.literal(PRESENCE_INDICATORS, "Indicador de presença inválido").default(1),
  postStock: z.boolean().default(true),
  postFinancial: z.boolean().default(true),
  financialCategoryId: nullish(ulidParam),
  additionalInfo: text(2000),
  fiscoInfo: text(2000),
  isDefault: z.boolean().default(false),
  active: z.boolean().default(true),
});
export type TaxOperationInput = z.output<typeof taxOperationInput>;
export type TaxOperationFormValues = z.input<typeof taxOperationInput>;

export const taxOperationListQuery = listQuery.extend({
  status: z.enum(["active", "inactive", "all"], "Situação inválida").default("active"),
});
export type TaxOperationListQuery = z.output<typeof taxOperationListQuery>;

const destUf = nullish(
  z
    .string()
    .trim()
    .toUpperCase()
    .refine((v): v is DestUf => (TAX_DEST_UFS as readonly string[]).includes(v), "UF inválida"),
).transform((v) => v as DestUf | null);

/** Regra de tributação: formato aqui; coerência com a direção da natureza (CFOP, CST × CSOSN, ST) no DO. */
export const taxRuleInput = z.object({
  priority: z
    .number()
    .int("Prioridade inteira")
    .min(0, "Prioridade de 0 a 1000")
    .max(1000, "Prioridade de 0 a 1000")
    .default(0),
  active: z.boolean().default(true),
  destUf,
  destIeIndicator: nullish(z.literal(IE_INDICATORS, "Indicador de IE inválido")).transform(
    (v) => v as IeIndicator | null,
  ),
  destFinalConsumer: nullish(z.boolean()),
  crt: nullish(z.literal(CRTS, "CRT inválido")).transform((v) => v as Crt | null),
  ncmPrefix: nullish(
    z
      .string()
      .trim()
      .transform((v) => v.replace(/\./g, "") || null)
      .pipe(
        z
          .string()
          .regex(/^\d{2,8}$/, "NCM (início) de 2 a 8 dígitos")
          .nullable(),
      ),
  ),
  productId: nullish(ulidParam),
  productGroupId: nullish(ulidParam),
  productOrigin: nullish(z.number().int().min(0, "Origem de 0 a 8").max(8, "Origem de 0 a 8")),
  cfop: z
    .string("Informe o CFOP")
    .trim()
    .regex(/^\d{4}$/, "CFOP com 4 dígitos"),
  icmsCst: code(/^\d{2}$/, "CST com 2 dígitos"),
  icmsCsosn: code(/^\d{3}$/, "CSOSN com 3 dígitos"),
  icmsPct4: pct4(),
  icmsReductionPct4: pct4(),
  icmsCreditSnPct4: pct4(),
  icmsDeferralPct4: pct4(),
  fcpPct4: pct4(),
  icmsStModBc: nullish(
    z.literal([4, 6], "Modalidade da base do ST: 4 (MVA) ou 6 (valor da operação)"),
  ).transform((v) => v as 4 | 6 | null),
  icmsStMvaPct4: pct4(MAX_MVA_PCT4),
  icmsStMvaAdjust: z.boolean().default(false),
  icmsStPct4: pct4(),
  icmsStReductionPct4: pct4(),
  fcpStPct4: pct4(),
  difal: z.boolean().default(true),
  difalDoubleBase: z.boolean().default(false),
  icmsDestPct4: pct4(),
  fcpDestPct4: pct4(),
  ipiCst: code(/^\d{2}$/, "CST com 2 dígitos"),
  ipiPct4: pct4(),
  ipiEnq: code(/^\d{3}$/, "Enquadramento com 3 dígitos"),
  pisCst: code(/^\d{2}$/, "CST com 2 dígitos"),
  pisPct4: pct4(),
  cofinsCst: code(/^\d{2}$/, "CST com 2 dígitos"),
  cofinsPct4: pct4(),
  pisCofinsExcludeIcms: z.boolean().default(true),
  ibsCst: code(/^\d{3}$/, "CST com 3 dígitos"),
  ibsClass: code(/^\d{6}$/, "cClassTrib com 6 dígitos"),
  ibsUfPct4: pct4(),
  ibsMunPct4: pct4(),
  cbsPct4: pct4(),
  ibsReductionPct4: pct4(),
  cbsReductionPct4: pct4(),
  ibsDeferralPct4: pct4(),
  isPct4: pct4(),
  infoText: text(2000),
});
export type TaxRuleInput = z.output<typeof taxRuleInput>;
export type TaxRuleFormValues = z.input<typeof taxRuleInput>;

export type TaxRuleView = TaxRuleDraft & {
  id: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

export type TaxOperationView = TaxOperationInput & {
  id: string;
  /** Regras ativas. */
  rulesCount: number;
  createdAt: string;
  updatedAt: string;
};
export type TaxOperationDetail = TaxOperationView & { rules: TaxRuleView[] };
export type TaxOperationListResult = { data: TaxOperationView[]; nextCursor: string | null };

export const taxWizardInput = z.object({
  regime: z.enum(TAX_REGIMES, "Escolha o regime tributário"),
  revenueBand: z.enum(REVENUE_BANDS, "Escolha a faixa de faturamento"),
  branch: z.enum(BUSINESS_BRANCHES, "Escolha o ramo"),
  activities: z
    .array(z.enum(WIZARD_ACTIVITIES, "Atividade inválida"), "Escolha as atividades")
    .min(1, "Escolha ao menos uma atividade")
    .max(WIZARD_ACTIVITIES.length)
    .refine((a) => new Set(a).size === a.length, "Atividade repetida"),
  creditSnPct4: pct4(),
});
export type TaxWizardInputBody = z.output<typeof taxWizardInput>;
export type TaxWizardResult = {
  crt: Crt;
  created: { id: string; name: string }[];
  /** Já existiam (pelo nome): nada foi alterado nelas. */
  skipped: string[];
};

const simulateItem = z
  .object({
    productId: nullish(ulidParam),
    /** Item avulso (sem produto): NCM e origem informados aqui. */
    ncm: code(/^\d{8}$/, "NCM com 8 dígitos"),
    origin: nullish(z.number().int().min(0, "Origem de 0 a 8").max(8, "Origem de 0 a 8")),
    qtyQ4: z
      .number("Informe a quantidade")
      .int("Quantidade inválida")
      .min(1, "A quantidade deve ser maior que zero")
      .max(MAX_STOCK_QTY_Q4, "Quantidade acima do limite"),
    unitPriceCents: cents("o preço", MAX_PRICE_CENTS),
    /** Total líquido do item (preço × quantidade − desconto do item); null = sem desconto. */
    totalCents: nullish(cents("o total")),
  })
  .superRefine((v, ctx) => {
    if (v.totalCents === null) return;
    const gross = (BigInt(v.unitPriceCents) * BigInt(v.qtyQ4) * 2n + 10_000n) / 20_000n;
    if (BigInt(v.totalCents) > gross)
      ctx.addIssue({
        code: "custom",
        path: ["totalCents"],
        message: "O total não pode passar de preço × quantidade",
      });
  });

export const taxSimulateInput = z
  .object({
    /** Natureza; null = a padrão do contato, senão a padrão de saída da empresa. */
    taxOperationId: nullish(ulidParam),
    contactId: nullish(ulidParam),
    /** Destinatário avulso (sem contato). */
    recipient: nullish(
      z.object({
        uf: z
          .string()
          .trim()
          .toUpperCase()
          .refine((v): v is DestUf => (TAX_DEST_UFS as readonly string[]).includes(v), "UF inválida")
          .transform((v) => v as DestUf),
        ieIndicator: z.literal(IE_INDICATORS, "Indicador de IE inválido").transform((v) => v as IeIndicator),
        finalConsumer: z.boolean(),
      }),
    ),
    /** Força consumidor final (ex.: contribuinte comprando para uso e consumo); null = pelo indicador de IE. */
    finalConsumer: nullish(z.boolean()),
    items: z
      .array(simulateItem, "Informe os itens")
      .min(1, "Inclua ao menos um item")
      .max(MAX_TAX_ITEMS, `No máximo ${MAX_TAX_ITEMS} itens`),
    discountCents: cents("o desconto").default(0),
    freightCents: cents("o frete").default(0),
    insuranceCents: cents("o seguro").default(0),
    otherExpensesCents: cents("as outras despesas").default(0),
  })
  .superRefine((v, ctx) => {
    if (v.contactId === null && v.recipient === null)
      ctx.addIssue({ code: "custom", path: ["contactId"], message: "Informe o contato ou o destinatário" });
  });
export type TaxSimulateInput = z.output<typeof taxSimulateInput>;

export type TaxSimulationItem = TaxResult["items"][number] & {
  productId: string | null;
  description: string | null;
  ncm: string | null;
  ruleId: string;
};
export type TaxSimulationResult = Omit<TaxResult, "items"> & {
  operation: { id: string; name: string };
  recipient: { uf: DestUf; ieIndicator: IeIndicator; finalConsumer: boolean };
  items: TaxSimulationItem[];
};

// ---------- perfil fiscal da empresa ----------

const ie = z
  .string()
  .trim()
  .transform((v) => {
    const t = v.toUpperCase();
    return t === "" ? null : t === "ISENTO" ? t : t.replace(/[.\-/\s]/g, "");
  })
  .pipe(
    z
      .string()
      .regex(/^(ISENTO|\d{2,14})$/, "IE com até 14 dígitos ou ISENTO")
      .nullable(),
  );

/** Endereço do emitente da NF-e (T2.3; a UF vai em `uf`). Código IBGE do município: 7 dígitos (vem da consulta do CEP). */
export const companyAddressInput = z.object({
  cep: blank
    .pipe(
      z
        .string()
        .regex(/^\d{5}-?\d{3}$/, "CEP inválido")
        .nullable(),
    )
    .transform((v) => v?.replace("-", "") ?? null),
  street: text(60),
  number: text(60),
  complement: text(60),
  district: text(60),
  city: text(60),
  cityIbge: blank.pipe(
    z
      .string()
      .regex(/^\d{7}$/, "Código IBGE com 7 dígitos")
      .nullable(),
  ),
});
export type CompanyAddress = z.output<typeof companyAddressInput>;

/** `PUT /fiscal/company` (T2.1 + T2.2 + T2.3): só os campos enviados mudam; ao menos um. */
export const companyFiscalInput = z
  .object({
    /** Mesmas regras do `PUT /fiscal/company` da T2.1 (CNPJ alfanumérico incluso). */
    cnpj: companyCnpjInput.shape.cnpj.optional(),
    ie: ie.optional(),
    uf: z
      .string()
      .trim()
      .toUpperCase()
      .refine((v) => (BRAZIL_UFS as readonly string[]).includes(v), "UF inválida")
      .optional(),
    crt: z.literal(CRTS, "CRT inválido").optional(),
    taxRegime: z.enum(TAX_REGIMES, "Regime inválido").optional(),
    revenueBand: z.enum(REVENUE_BANDS, "Faixa inválida").optional(),
    businessBranch: z.enum(BUSINESS_BRANCHES, "Ramo inválido").optional(),
    tradeName: text(60).optional(),
    im: blank
      .pipe(
        z
          .string()
          .regex(/^[0-9A-Za-z.\-/]{1,15}$/, "Inscrição municipal inválida")
          .nullable(),
      )
      .optional(),
    cnae: blank
      .pipe(
        z
          .string()
          .regex(/^\d{7}$/, "CNAE com 7 dígitos")
          .nullable(),
      )
      .optional(),
    phone: blank
      .pipe(
        z
          .string()
          .regex(/^[\d()\s+-]{8,20}$/, "Telefone inválido")
          .nullable(),
      )
      .optional(),
    email: blank.pipe(z.email("E-mail inválido").max(254).nullable()).optional(),
    address: companyAddressInput.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), "Informe ao menos um campo");
export type CompanyFiscalInput = z.output<typeof companyFiscalInput>;

export type CompanyFiscalProfile = {
  legalName: string;
  cnpj: string | null;
  ie: string | null;
  uf: string | null;
  crt: Crt;
  taxRegime: (typeof TAX_REGIMES)[number] | null;
  revenueBand: (typeof REVENUE_BANDS)[number] | null;
  businessBranch: (typeof BUSINESS_BRANCHES)[number] | null;
  tradeName: string | null;
  im: string | null;
  cnae: string | null;
  phone: string | null;
  email: string | null;
  address: CompanyAddress;
};
